import { MouseEvent, useState } from 'react';
import { CopyOutlined, CheckOutlined } from '@ant-design/icons';
import { useDomainModal } from '../DomainModalContext';

interface DomainNameProps {
  name?: string | null;
  /** Optional href to wrap with a link. When set, takes precedence over the modal click handler. */
  href?: string;
  /** Set to false to render plain text without click-to-open-modal behavior. */
  clickable?: boolean;
  /** Show a copy icon on hover. */
  copyable?: boolean;
}

/** Renders a domain with the TLD muted (e.g. "crypto.io" → "crypto" + ".io").
 *  By default clicking the domain opens the global Domain Info modal. */
export function DomainName({ name, href, clickable = true, copyable = true }: DomainNameProps) {
  const { open } = useDomainModal();
  const [copied, setCopied] = useState(false);

  if (!name) return <span className="text-text-3">—</span>;
  const dot = name.indexOf('.');
  const sld = dot === -1 ? name : name.slice(0, dot);
  const tld = dot === -1 ? '' : name.slice(dot);

  const content = (
    <>
      {sld}
      {tld && <span className="opacity-40">{tld}</span>}
    </>
  );

  function handleCopy(e: MouseEvent) {
    e.stopPropagation();
    e.preventDefault();
    navigator.clipboard.writeText(name as string);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  }

  const copyIcon = copyable ? (
    <span
      role="button"
      onClick={handleCopy}
      className="opacity-0 group-hover/dn:opacity-100 transition-opacity ml-1 text-text-3 hover:text-accent cursor-pointer align-middle"
      title="Copy domain"
    >
      {copied ? <CheckOutlined style={{ fontSize: 11 }} /> : <CopyOutlined style={{ fontSize: 11 }} />}
    </span>
  ) : null;

  if (href) {
    return (
      <span className="group/dn select-text text-text font-semibold">
        <a href={href} className="no-underline hover:text-accent [&:hover_span]:text-accent [&:hover_span]:opacity-100">
          {content}
        </a>
        {copyIcon}
      </span>
    );
  }

  if (clickable) {
    const handleClick = (e: MouseEvent<HTMLSpanElement>) => {
      if (window.getSelection()?.toString()) return;
      e.stopPropagation();
      open(name as string);
    };
    return (
      <span className="group/dn select-text text-text font-semibold">
        <span
          role="button"
          onClick={handleClick}
          className="cursor-pointer hover:text-accent [&:hover_span]:text-accent [&:hover_span]:opacity-100"
        >
          {content}
        </span>
        {copyIcon}
      </span>
    );
  }

  return (
    <span className="group/dn select-text text-text font-semibold">
      {content}
      {copyIcon}
    </span>
  );
}
